import { Button, Checkbox, Col, Collapse, DatePicker, Form, Select, Input, message, Radio, Row, Tooltip, Space, Popconfirm, InputNumber, Tag } from "antd";
import { useEffect, useState, useRef, useMemo } from "react";
import MaskedInput from "../../extensions/masked-input.js";
import { serverFetch } from "../../server";
import { dictsGetPaymentVids } from "../../extensions/dicts";
import { PaymentAdd } from "../payments";
import TakePhoto from "../../controls/take-photo/take-photo";
import UploadPhoto from "../../controls/take-photo/upload-photo.js";
import dayjs from "dayjs";
import { LogMonitor, Scan } from "@controls";
import { isMobile } from "../../extensions/design";
import ButtonReport from "../../controls/button-report/button-report.js";
import ScanMobile from "@/controls/scan/scan-mobile.js";
import { TAG_OBJECT_VID_CLIENTS } from "../settings/tags/tags-const";
import { handleOnlyNumbersKeyDown, ONLY_NUMBERS_PARSER } from '../../extensions/utils';

/** HEX для тегов клиента (#…) */
const clientTagHexColor = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

const ClientShared = ({ id, onClose, exposeOnSave, onFormValidChange, initialFields, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [item, setItem] = useState({ vid: 0 });
  const [showPayment, setShowPayment] = useState(false);
  const [takePhoto, setTakePhoto] = useState(false);
  const [uploadPhoto, setUploadPhoto] = useState(false);
  const [trainerUsers, setTrainerUsers] = useState();
  const [managerUsers, setManagerUsers] = useState();
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [imageVersion, setImageVersion] = useState(Date.now());
  const imageRef = useRef(null);
  const itemRef = useRef(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [scanActive, setScanActive] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [vidPayments, setVidPayments] = useState([]);
  const [depositAction, setDepositAction] = useState(null);
  const [certificateAction, setCertificateAction] = useState(null);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const initialFieldsAppliedRef = useRef(false);

  itemRef.current = item;

  const sexOptions = !isMobile ? [
    { label: "Мужской", value: 1 },
    { label: "Женский", value: 0 },
  ] : [
    { label: "Муж.", value: 1 },
    { label: "Жен.", value: 0 },
  ];

  useEffect(() => {
    initialFieldsAppliedRef.current = false;
  }, [id]);

  /** Предзаполнение из родителя при создании клиента (id === 0), один раз за открытие модалки */
  useEffect(() => {
    if (id !== 0 || initialFieldsAppliedRef.current || !initialFields) return;
    const patch = {};
    if (initialFields.name?.trim()) patch.name = initialFields.name.trim();
    if (initialFields.phone?.trim()) patch.phone = initialFields.phone.trim();
    if (initialFields.email?.trim()) patch.email = initialFields.email.trim();
    if (Object.keys(patch).length === 0) return;
    initialFieldsAppliedRef.current = true;
    form.setFieldsValue(patch);
    setItem((prev) => ({ ...prev, ...patch }));
  }, [id, initialFields, form]);

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`client/${id}`)
        .then((data) => {
          if (data.createdOn) data.createdOn = dayjs(data.createdOn);
          if (data.dateBirth) data.dateBirth = dayjs(data.dateBirth);
          data.vid = data.vid === 1;
          setLoading(false);
          setItem(data);
          const tagIds = (Array.isArray(data.tags) ? data.tags : [])
            .filter((t) => Number(t.objectVid) === TAG_OBJECT_VID_CLIENTS)
            .map((t) => t.id);
          const { tags: _tagsDrop, ...rest } = data;
          form.setFieldsValue({ ...rest, tagIds });
        })
        .catch((e) => {
          setLoading(false);
          message.error(e.userName);
        });
    }
  };
  useEffect(getData, [form, id]);

  const getAccounts = () => {
    if (id) {
      serverFetch(`payment/accounts/client/${id}`)
        .then(data => { setAccounts(data); })
        .catch(e => message.error(e.userMessage));
    }
  };
  useEffect(getAccounts, [id]);

  useEffect(() => { dictsGetPaymentVids(data => { setVidPayments(data); }); }, []);

  useEffect(() => {
    setLoadingTags(true);
    serverFetch("Tag", {
      queryParams: { objectVid: [TAG_OBJECT_VID_CLIENTS] },
    })
      .then((list) => setTagCatalog(Array.isArray(list) ? list : []))
      .catch(() => setTagCatalog([]))
      .finally(() => setLoadingTags(false));
  }, []);

  const { tagSelectOptions, tagById } = useMemo(() => {
    const opts = tagCatalog.map((t) => ({
      value: t.id,
      label: t.value ?? "",
      tagColor: t.color,
    }));
    const map = new Map(tagCatalog.map((t) => [t.id, t]));
    return { tagSelectOptions: opts, tagById: map };
  }, [tagCatalog]);

  const onDepositSubmit = () => {
    if (!depositAction?.syma || !depositAction?.paymentVidId) {
      message.warning("Заполните сумму и кассу");
      return;
    }
    const body = {
      clientId: id,
      paymentVidId: depositAction.paymentVidId,
      paymentVid2Id: depositAction.paymentVid2Id,
      transactionVid: depositAction.type === 'Payment' ? 1 : -1,
      syma: depositAction.syma,
      comment: depositAction.comment,
    };
    setLoading(true);
    serverFetch("payment/deposit", {
      method: "POST",
      bodyData: body,
    })
      .then(() => {
        setDepositAction(null);
        setLoading(false);
        getAccounts();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const onCertificateSubmit = () => {
    if (!certificateAction?.saleId || !certificateAction?.saleClientId) {
      message.warning("Заполните номер сертификата и проверочный код");
      return;
    }
    const body = {
      saleId: certificateAction.saleId,
      saleClientId: certificateAction.saleClientId,
      clientId: id,
      paymentVidId: certificateAction.paymentVidId
    };
    console.log(body)
    setLoading(true);
    let saleData = null;

    serverFetch(`sale/${certificateAction?.saleId}`)
      .then(response => {
        saleData = response;
        
        if (response?.deleted === true) {
          message.error("Погашение невозможно: сертификат удален");
          setLoading(false);
        } else {
          serverFetch("sale/certificate/use", {
            method: "POST",
            bodyData: body,
          })
            .then(() => {
              setCertificateAction(null);
              setLoading(false);
              getAccounts();
            })
            .catch(e => {
              message.error(e.userMessage);
              setLoading(false);
            });
        }
      })
      .catch(error => {
        console.error('Ошибка:', error);
      });
  };

  const getUsers = () => {
    setLoading(true);
    setUsersLoaded(false);
    serverFetch(`User`)
      .then((data) => {
        setLoading(false);
        const options = data.map((u) => ({
          label: u.name,
          value: u.id,
        }));
        setTrainerUsers(options); // In future there will be separating or smth
        setManagerUsers(options);
        setUsersLoaded(true);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userName);
      });
  };
  useEffect(getUsers, [form, id]);

  /** После загрузки списков тренеров/менеджеров синхронизировать форму с item; для нового клиента (id===0) мержить, чтобы не затереть поля из предзаполнения (напр. почта). */
  useEffect(() => {
    if (!usersLoaded) return;
    const hasAnySeedField = Boolean(item?.name ?? item?.phone ?? item?.email);
    if (!hasAnySeedField) return;
    const cur = form.getFieldsValue();
    const merged = { ...cur };
    Object.entries(item).forEach(([k, v]) => {
      if (v !== undefined) merged[k] = v;
    });
    delete merged.tags;
    form.setFieldsValue(merged);
  }, [usersLoaded, item, form]);

  useEffect(() => {
    if (onFormValidChange) {
      onFormValidChange(isFormValid);
    }
  }, [isFormValid]);
  

  const onClosePhoto = (refresh) => {
    setTakePhoto(false);
    refresh && getData();
  }
  const onCloseUpload = (refresh) => {
    setUploadPhoto(false);
    refresh && getData();
  }

  const onSavePhoto = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    serverFetch(`client/photo/${id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        setImageVersion(Date.now());
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsFormValid(!hasErrors);
  };

  const onDeletePhoto = () => {
    const formData = new FormData();
    serverFetch(`client/photo/${id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        setImageVersion(Date.now());
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };
  const onDeleteClient = () => {
    setLoading(true);
    serverFetch(`client/${id}`, { method: "DELETE" })
      .then(() => {
        onClose(true);
        setLoading(false);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onValuesChange = (changedValues) => {
    setItem((prev) => {
      const next = { ...prev, ...changedValues };
      delete next.tagIds;
      return next;
    });
  };

  const onClosePayment = (refresh) => {
    setShowPayment(false);
    getData();
  }
  
  const onSave = () => {
    setLoading(true); 
    const isNew = id;
    const tagIdsSelected = form.getFieldValue("tagIds") ?? [];
    const payload = { ...item, vid: item.vid ? 1 : 0 };
    delete payload.tags;
    delete payload.tagIds;
    payload.tags = Array.isArray(tagIdsSelected) ? tagIdsSelected.map((tid) => ({ id: tid })) : [];
    serverFetch("client", {
      method: isNew ? "PUT" : "POST",
      bodyData: payload,
    })
      .then((response) => {
        if (!isNew) {
          onClose(true, response?.id, response?.name);
          setLoading(false);
        } else if (onSaved) {
          onSaved(response ?? payload);
          getData();
        } else {
          setLoading(false);
        }
      })
      .catch((e) => { 
        message.error(e.userMessage); 
        setLoading(false); 
      })
  };

  useEffect(() => {
    if (exposeOnSave) 
      exposeOnSave(() => onSave);
  }, [exposeOnSave, id, item, onSaved]);

  const handleScan = (card) => {
    setItem({ ...itemRef.current, card });
    form.setFieldsValue({ card });
    setScanActive(false)
  };

  const onCancel = () => onClose();
  const { Panel } = Collapse;
  const depositAccountId = vidPayments.find(v => v.fiscal === 21)?.id;
  const certificateAccountId = vidPayments.find(v => v.fiscal === 22)?.id;

  const openDepositAction = (account, type) => {
    if (depositAction?.accountId === account.id && depositAction?.type === type) {
      setDepositAction(null);
    } else {
      setDepositAction({ accountId: account.id, paymentVid2Id: account.id, type, syma: type === 'Return' ? account.syma : '', paymentVidId: null, comment: '' });
    }
  };

const renderDepositForm = () => {
  if (isMobile) return (
  <div className="client-deposit-operation-form"
  style={{ padding:"8px", flexDirection: "column", alignItems: "stretch", gap: "12px", marginBottom: "6px"}}>
    <div style={{ display: "flex", gap: "8px", flexDirection: "row" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Сумма</span>
        <InputNumber value={depositAction?.syma} onChange={v => setDepositAction({ ...depositAction, syma: v })} style={{ width: isMobile ? "100%" : 80 }} 
          precision={2} placeholder="0.00" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
        {!isMobile && <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Касса:</span>}
        <Select placeholder="Касса" value={depositAction?.paymentVidId} style={{ width: isMobile ? "100%" : 140 }}
          options={vidPayments.filter(option => option.fiscal !== 21 && option.fiscal !== 22 && option.fiscal !== 23)} 
          onChange={v => setDepositAction({ ...depositAction, paymentVidId: v })} 
        />
      </div>
    </div>
    <Input placeholder="Комментарий" value={depositAction?.comment} onChange={e => setDepositAction({ ...depositAction, comment: e.target.value })} style={{ flex: 1, marginTop: -6 }} />
    <div style={{ display: "flex", gap: "4px", justifyContent: "space-between", marginTop: -6}}>
      <Button onClick={onDepositSubmit} loading={loading} style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? "auto" : "80px" }}> ОК </Button>
      <Button onClick={() => setDepositAction(null)} style={{ flex: isMobile ? 1 : "none" }}>Отмена</Button>
    </div>
  </div>
  );
  return (
    <div style={{ marginBottom: 8, marginTop: 8, paddingTop: 8, paddingRight: 8, backgroundColor: "#eff2ee", border: "1px solid #dddddd", borderRadius: "10px"}}>
      <Form.Item label={"Сумма"} style={{ marginBottom: 8 }}>
        <InputNumber value={depositAction?.syma} onChange={v => setDepositAction({ ...depositAction, syma: v })} style={{ width: 147 }} precision={2}  stringMode={false} placeholder="0.00" />
      </Form.Item>
      <Form.Item label="Касса" style={{ marginBottom: 8 }}>
        <Select options={vidPayments.filter(option => option.fiscal !== 21 && option.fiscal !== 22 && option.fiscal !== 23)} value={depositAction?.paymentVidId} onChange={v => setDepositAction({ ...depositAction, paymentVidId: v })} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item label="Комментарий" style={{ marginBottom: 8 }}>
        <Input value={depositAction?.comment} onChange={e => setDepositAction({ ...depositAction, comment: e.target.value })} />
      </Form.Item>
      <Form.Item label=" ">
        <Space>
          <Button style={{width: "150px"}} onClick={onDepositSubmit} loading={loading}>ОК</Button>
          <Button style={{width: "150px"}} onClick={() => setDepositAction(null)}>Отмена</Button>
        </Space>
      </Form.Item>
    </div>
  )
};

const renderCertificateForm = () => {
  if (isMobile) return (
  <div className="client-deposit-operation-form" style={{ padding:"6px 8px 6px 6px", flexDirection: "column", alignItems: "stretch", gap: "6px", marginBottom: "4px"}}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Номер серт.</span>
      <InputNumber value={certificateAction?.saleId} onChange={v => setCertificateAction({ ...certificateAction, saleId: v })} parser={ONLY_NUMBERS_PARSER} onKeyDown={handleOnlyNumbersKeyDown} style={{ width: "100%"}} />
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Провер. код</span>
      <InputNumber value={certificateAction?.saleId} onChange={v => setCertificateAction({ ...certificateAction, saleId: v })} parser={ONLY_NUMBERS_PARSER} onKeyDown={handleOnlyNumbersKeyDown} style={{ width: "100%"}} />
    </div>
    <div style={{ display: "flex", gap: "6px", justifyContent: "space-between", marginTop: "-2px"}}>
      <Button onClick={onCertificateSubmit} loading={loading} style={{ flex: 1, minWidth: "auto"}}> ОК </Button>
      <Button onClick={() => setCertificateAction(null)} style={{ flex: 1 }}>Отмена</Button>
    </div>
  </div>
  );
  return (
    <div style={{ marginBottom: 8, marginTop: 8, paddingTop: 8, paddingRight: 8, backgroundColor: "#eff2ee", border: "1px solid #dddddd", borderRadius: "10px"}}>
      <Form.Item label={"Номер сертификата"} style={{ marginBottom: 8 }}>
        <InputNumber value={certificateAction?.saleId} onChange={v => setCertificateAction({ ...certificateAction, saleId: v })} parser={ONLY_NUMBERS_PARSER} onKeyDown={handleOnlyNumbersKeyDown} style={{ width: 147 }}/>
      </Form.Item>
      <Form.Item label="Проверочный код" style={{ marginBottom: 8 }}>
        <InputNumber value={certificateAction?.saleClientId} onChange={v => setCertificateAction({ ...certificateAction, saleClientId: v })} parser={ONLY_NUMBERS_PARSER} onKeyDown={handleOnlyNumbersKeyDown}/>
      </Form.Item>
      <Form.Item label=" ">
        <Space>
          <Button style={{width: "150px"}} onClick={onCertificateSubmit} loading={loading}>ОК</Button>
          <Button style={{width: "150px"}} onClick={() => setCertificateAction(null)}>Отмена</Button>
        </Space>
      </Form.Item>
    </div>
  )
};

  const clientCard  = !isMobile ? <>
  <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 24 }} colon={false} disabled={loading || loadingTags} onValuesChange={onValuesChange}
      onFieldsChange={() => {
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
        setIsFormValid(!hasErrors);
      }}
    >
      <Form.Item label="ФИО" name="name" rules={[{ required: true, message: "Введите ФИО" }]} validateTrigger="onBlur"><Input /></Form.Item>
      <Form.Item className="sport-modal-group-item">
        <Row gutter={8}>
          <Col span={16}>
              <div className="client-scan">
                <Form.Item label="Карта" name="card" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}><MaskedInput maskType="card"/></Form.Item>
                <Tooltip title="Просканируйте карту клиента"><img src="/images/scan.svg" style={{width: 32}} alt="scan"/></Tooltip>
              </div>
            <Form.Item label="Телефон" name="phone" labelCol={{ span: 9 }} wrapperCol={{ span: 15 }}>
              <MaskedInput maskType="phone" />
            </Form.Item>
            <Form.Item label="Дата рождения" name="dateBirth" labelCol={{ span: 9 }} wrapperCol={{ span: 15 }}><DatePicker format="DD.MM.YYYY"/></Form.Item>
            <Form.Item label="Пол" name="sex" labelCol={{ span: 9 }} wrapperCol={{ span: 15 }}><Radio.Group options={sexOptions} /></Form.Item>
            <Form.Item label="Дата регистрации" name="createdOn" labelCol={{ span: 9 }} wrapperCol={{ span: 15 }}><DatePicker format="DD.MM.YYYY" disabled/></Form.Item>
          </Col>
          <Col span={8}>
            <div className="client-photo">
              <img ref={imageRef} src={item?.imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.imageId}.jpg` : "/images/sample.png"}  alt="client" className="client-photo-picture" />
              {id !== 0 && <img src="/images/photo.svg" alt="take-photo" className="client-photo-button" onClick={() => setTakePhoto(true)} />}
              {id !== 0 && <img src="/images/upload-photo.svg" alt="upload-photo" className="client-photo-upload-button" onClick={() => setUploadPhoto(true)} />}
              {id !== 0 && <img src="/images/delete-button.svg" alt="delete-photo" className="client-photo-delete-button" onClick={() => onDeletePhoto()} />}
            </div>
            {takePhoto && <TakePhoto onClose={onClosePhoto} onSave={onSavePhoto} />}
            {uploadPhoto && <UploadPhoto onClose={onCloseUpload} onSave={onSavePhoto} />}
          </Col>
        </Row>
      </Form.Item>
      <Form.Item label="Почта" name="email"><MaskedInput maskType="email" placeholder="mail@example.com" /></Form.Item>
      <Form.Item label="Теги" name="tagIds">
        <Select
          mode="multiple"
          allowClear
          placeholder="Выберите теги"
          options={tagSelectOptions}
          optionFilterProp="label"
          showSearch
          style={{ width: "100%" }}
          optionRender={(oriOption) => {
            const d = oriOption.data;
            const hex = clientTagHexColor(d?.tagColor);
            return (
              <span className="client-tag-option-row">
                <span className="client-tag-swatch" aria-hidden style={{ background: hex ?? "#d9d9d9" }} />
                <span>{d?.label ?? ""}</span>
              </span>
            );
          }}
          tagRender={(props) => {
            const { label, value, closable, onClose } = props;
            const meta = tagById.get(value);
            const hex = clientTagHexColor(meta?.color);
            return (
              <Tag
                closable={closable}
                onClose={onClose}
                style={{
                  marginInlineEnd: 4,
                  ...(hex
                    ? {
                        backgroundColor: hex,
                        borderColor: hex,
                        color: "#fff",
                      }
                    : {}),
                }}
              >
                {meta?.value ?? label}
              </Tag>
            );
          }}
        />
      </Form.Item>
      <Form.Item label="Тренер" name="userId"><Select options={trainerUsers} placeholder="Выберите тренера" /></Form.Item>
      <Form.Item label="Менеджер" name="managerId"><Select options={managerUsers} placeholder="Выберите менеджера" /></Form.Item>

      <Form.Item label=" " labelCol={{span:6}} className="sport-modal-group-item">
        <Collapse ghost style={{ marginLeft: -16 }}>
          <Panel header="Дополнительно" key="notifications">
              <Form.Item label="Диалоги" labelCol={{ span: 6 }} className="sport-modal-group-item" style={{ marginBottom: 4 }}>
                <Row>
                  <Form.Item name="sendVk" valuePropName="checked"><Checkbox>ВКонтакте</Checkbox></Form.Item>
                  <Form.Item name="sendMAX" valuePropName="checked"><Checkbox>MAX</Checkbox></Form.Item>
                  <Form.Item name="sendSms" valuePropName="checked"><Checkbox>СМС</Checkbox></Form.Item>
                </Row>
              </Form.Item>
              <Form.Item label="Сообщ. в визите" name="comment" labelCol={{ span: 6 }}><Input /></Form.Item>
              <Form.Item label=" " name="showMessageOnStart" valuePropName="checked" labelCol={{ span: 6 }}><Checkbox>Показывать сообщение при начале визита</Checkbox></Form.Item>
            <Form.Item labelCol={{ span: 6 }} label="Паспорт" name="passport"><Input /></Form.Item>
            <Form.Item labelCol={{ span: 6 }} label="Адрес" name="address"><Input /></Form.Item>
          </Panel>
        </Collapse>
      </Form.Item>
      <div className="client-accounts-block">
        <div style={{ marginLeft: 16, marginBottom: 4, fontWeight: 800 }}>Счета клиента</div>
        {accounts.map(account => ( //Показыаем все счета
          <div key={account.id}>
            <Form.Item label={account.name} className="sport-modal-group-item" style={{marginBottom: 4}}>
              <Row gutter={8} align="middle" style={{ width: "100%" }}>
                <Col flex="auto">
                  <Input disabled value={account.syma?.toFixed(2)} style={{ width: "100%" }}/>
                </Col>

                <Col flex="280px" style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px' }}>
                  {account.id === depositAccountId && (
                    <>
                      <Button type={depositAction?.accountId === account.id && depositAction?.type === 'Payment' ? 'primary' : 'default'} onClick={() => openDepositAction(account, 'Payment')} style={{ width: '100%' }}>Пополнить</Button>
                      <Button type={depositAction?.accountId === account.id && depositAction?.type === 'Return' ? 'primary' : 'default'} onClick={() => openDepositAction(account, 'Return')} style={{ width: '100%' }}>Вернуть</Button>
                    </>
                  )}
                  {account.id === certificateAccountId && (
                    <Button onClick={() => setCertificateAction({ accountId: account.id, saleId: null, saleClientId: null, clientId: item?.clientId, visitId: null, paymentVidId: accounts?.find(v => v.fiscal === 22)?.id})} style={{ width: '100%' }}>Погасить сертификат</Button>
                  )}
                </Col>
              </Row>
            </Form.Item>
            {depositAction?.accountId === account.id && renderDepositForm()}
            {certificateAction?.accountId === account.id && renderCertificateForm()}
          </div>
        ))}
      </div>
      <Form.Item label=" " name="vid" valuePropName="checked"><Checkbox>Юридическое лицо</Checkbox></Form.Item>
      {item.vid ? (
        <>
          <Form.Item label="Полн.наименование" name="fullName"><Input /></Form.Item>
          <Form.Item label="Юридический адрес" name="legalAddress"><Input /></Form.Item>
          <Form.Item label="Фактический адрес" name="addressFact"><Input /></Form.Item>
          <Form.Item className="sport-modal-group-item" label="Телефон">
            <Row>
              <Col span={10}>
                <Form.Item name="legalEntityPhone" wrapperCol={{ span: 24 }}>
                  <MaskedInput maskType="phone" />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item label="ИНН/КПП" name="inn" maxLength={20} labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                  <MaskedInput maskType="innKpp" />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item className="sport-modal-group-item" label="Расчетный счет">
            <Row>
              <Col span={10}>
                <Form.Item name="rschet" maxLength={20} wrapperCol={{ span: 24 }}>
                  <MaskedInput maskType="rschet" />
                </Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item label="к/счет" maxLength={20} name="kschet" labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                  <MaskedInput maskType="kschet" />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item className="sport-modal-group-item" label="БИК" maxLength={9}>
            <Row>
              <Col span={10}>
                <Form.Item name="bik" wrapperCol={{ span: 24 }}><MaskedInput maskType="bik" /></Form.Item>
              </Col>
              <Col span={14}>
                <Form.Item label="ОГРН" name="ogrn" maxLength={13} labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                  <MaskedInput maskType="orgn" />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item label="Банк" name="bank"><Input /></Form.Item>
          <Form.Item label="ФИО руководителя" name="sotrName"><Input /></Form.Item>
          <Form.Item label="Должность" name="dolj"><Input /></Form.Item>
          <Form.Item label="ФИО бухгалтера" name="buhName"><Input /></Form.Item>
        </>
      ) : null}
      <Form.Item label=" " labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
        <div className="client-shared-bottom_btns">
            {(!item.clientId && item.dolg) ? <Button onClick={() => setShowPayment(true)} className="sport-btn-wide-alt">Долг {item.dolg}</Button> : null}
              <Button className="sport-btn-wide" onClick={onSave} disabled={!isFormValid}>Сохранить</Button>
              <Popconfirm title="Подтверждение" description="Удалить клиента?" onConfirm={onDeleteClient}><Button className="sport-btn-wide-alt">Удалить</Button></Popconfirm>
              <ButtonReport vid={1} id={id}/>
              <LogMonitor vid={1} id={id}/>
              {!isMobile ? (<Button className="sport-btn-wide" onClick={onCancel}>Закрыть</Button>): (<></>) }
        </div>
      </Form.Item>
    </Form>
    </> : 
    <>
      <Form form={form} layout="horizontal" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1" }} style={{padding:"8px"}} disabled={loading || loadingTags} onValuesChange={onValuesChange}
        onFieldsChange={() => {
          const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
          setIsFormValid(!hasErrors);
        }}
      >
        <Form.Item style={{marginBottom: "4px"}} label="ФИО" name="name" rules={[{ required: true, message: "Введите ФИО" }]}><Input /></Form.Item>
        <Form.Item className="sport-modal-group-item">
        <div className="client-flexible-container-for-forms">
          <Form.Item style={{marginBottom: "4px", width: "100%"}} label="Карта" name="card" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}} >
                <MaskedInput maskType="card" style={{width:"100%"}}/>
          </Form.Item>
          <Tooltip title="Просканируйте карту клиента"><img src="/images/scan.svg" style={{width: 32}} alt="scan" onClick={() => setScanActive(true)}/></Tooltip>
        </div>
        <div className="client-scan-mobile">
          <div>
            <Form.Item style={{marginBottom: "4px"}} label="Телефон" name="phone" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1" }} ><MaskedInput maskType="phone" style={{width:"100%"}}/></Form.Item>
            <Form.Item style={{marginBottom: "4px"}} label="Дата рож." name="dateBirth" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1" }} ><DatePicker format="DD.MM.YYYY" style={{width:"100%"}}/></Form.Item>
            <Form.Item style={{marginBottom: "4px"}} label="Пол" name="sex" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1" }}><Radio.Group options={sexOptions} style={{width:"100%"}}/></Form.Item>
            <Form.Item style={{marginBottom: "4px"}} label="Дата рег." name="createdOn" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1" }}><DatePicker format="DD.MM.YYYY" style={{width:"100%"}} disabled/></Form.Item>
          </div>
          <div>
            <div className="client-photo-mobile">
              <img ref={imageRef} src={item?.imageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.imageId}.jpg?v=${imageVersion}` : "/images/sample.png"} alt="client" className="client-photo-picture-mobile" />
            </div>
            {id !== 0 && (
                <div className="client-photo-actions">
                  <img src="/images/photo.svg" alt="take-photo" onClick={() => setTakePhoto(true)}/>
                  <img src="/images/upload-photo.svg" alt="upload-photo" onClick={() => setUploadPhoto(true)}/>
                  <img src="/images/delete-button.svg" alt="delete-photo" onClick={onDeletePhoto}/>
                </div>
              )}
          </div>
        </div>
        <Form.Item style={{marginBottom: "4px"}} label="Почта" name="email" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}} ><MaskedInput maskType="email" placeholder="mail@example.com" style={{width:"100%"}} /></Form.Item>
        <Form.Item style={{marginBottom: "4px"}} label="Теги" name="tagIds" labelCol={{ flex: "80px" }} wrapperCol={{ flex: "1" }}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Выберите теги"
            options={tagSelectOptions}
            optionFilterProp="label"
            showSearch
            style={{ width: "100%" }}
            optionRender={(oriOption) => {
              const d = oriOption.data;
              const hex = clientTagHexColor(d?.tagColor);
              return (
                <span className="client-tag-option-row">
                  <span className="client-tag-swatch" aria-hidden style={{ background: hex ?? "#d9d9d9" }} />
                  <span>{d?.label ?? ""}</span>
                </span>
              );
            }}
            tagRender={(props) => {
              const { label, value, closable, onClose } = props;
              const meta = tagById.get(value);
              const hex = clientTagHexColor(meta?.color);
              return (
                <Tag
                  closable={closable}
                  onClose={onClose}
                  style={{
                    marginInlineEnd: 4,
                    ...(hex
                      ? {
                          backgroundColor: hex,
                          borderColor: hex,
                          color: "#fff",
                        }
                      : {}),
                  }}
                >
                  {meta?.value ?? label}
                </Tag>
              );
            }}
          />
        </Form.Item>
        <Form.Item style={{marginBottom: "4px"}} label="Тренер" name="userId" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}><Select options={trainerUsers} placeholder="Выберите тренера" style={{width:"100%"}} /></Form.Item>
        <Form.Item style={{marginBottom: "4px"}} label="Менеджер" name="managerId" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}><Select options={managerUsers} placeholder="Выберите менеджера" style={{width:"100%"}} /></Form.Item>

        <Collapse ghost style={{ marginLeft: -16 }} expandIcon={({ isActive }) => ( <span style={{ display: "inline-block", marginLeft: 15, transform: isActive ? "rotate(90deg)" : "rotate(0deg)" }}> {">"} </span>)}>
          <Panel style={{marginLeft: 6}} header="Дополнительно" key="notifications">
              <Form.Item style={{marginBottom: "4px"}} label="Диалоги" className="sport-modal-group-item" labelCol={{ flex: "100px" }}  wrapperCol={{ flex: "1"}}>
                <Row>
                  <Form.Item style={{marginBottom: "4px"}} name="sendVk" valuePropName="checked"><Checkbox><span style={{fontSize:"12px"}}>ВКонтакте</span></Checkbox></Form.Item>
                  <Form.Item style={{marginBottom: "4px"}} name="sendMax" valuePropName="checked"><Checkbox><span style={{fontSize:"12px"}}>MAX</span></Checkbox></Form.Item>
                  <Form.Item style={{marginBottom: "4px"}} name="sendSms" valuePropName="checked"><Checkbox><span style={{fontSize:"12px"}}>СМС</span></Checkbox></Form.Item>
                </Row>
              </Form.Item>
              <Form.Item style={{marginBottom: "4px"}} label="Сообщение" name="comment" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}><Input /></Form.Item>
              <Form.Item style={{marginBottom: "4px"}} label=" " name="showMessageOnStart" valuePropName="checked" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}>
              <Checkbox><span style={{fontSize:"11px"}}>Показывать сообщ. при начале визита</span></Checkbox>
            </Form.Item>
            <Form.Item style={{marginBottom: "4px"}} label="Паспорт" name="passport" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}><Input style={{width:"100%"}} /></Form.Item>
            <Form.Item style={{marginBottom: "4px"}} label="Дом. адрес" name="address" labelCol={{ flex: "80px" }}  wrapperCol={{ flex: "1"}}><Input style={{width:"100%"}} /></Form.Item>
          </Panel>
        </Collapse>
        <div className="client-accounts-block client-accounts-block-mobile">
          <div style={{ marginLeft: 4, fontWeight: 600, fontSize: 12 }}>Счета клиента</div>
          {accounts.map(account => ( //Показыаем все счета
          <div key={account.id}>
            <Form.Item key={account.id} label={account.name}  style={{ marginBottom: 4 }} className="client-item-line-deposit" labelCol={{ flex: "100px" }}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', width: '93%' }}>
                <Input disabled value={account.syma?.toFixed(2)}/>
                {account.id === depositAccountId && (
                  <>
                    <Button type={depositAction?.accountId === account.id && depositAction?.type === 'Payment' ? 'primary' : 'default'} onClick={() => openDepositAction(account, 'Payment')} style={{ padding: "0 8px" }}>Пополн.</Button>
                    <Button type={depositAction?.accountId === account.id && depositAction?.type === 'Return' ? 'primary' : 'default'} onClick={() => openDepositAction(account, 'Return')} style={{ padding: "0 8px" }}>Возвр.</Button>
                  </>
                )}
                {account.id === certificateAccountId && (
                  <Button onClick={() => setCertificateAction({ accountId: account.id, saleId: null, saleClientId: null, clientId: item?.clientId, visitId: null, paymentVidId: accounts?.find(v => v.fiscal === 22)?.id})} style={{ width: '100%' }}>Погасить</Button>
                )}
              </div>
            </Form.Item>
            {depositAction?.accountId === account.id && renderDepositForm()}
            {certificateAction?.accountId === account.id && renderCertificateForm()}
          </div>
        ))}
        </div>
      <Col span={8}>
        {takePhoto && <TakePhoto onClose={onClosePhoto} onSave={onSavePhoto} />}
        {uploadPhoto && <UploadPhoto onClose={onCloseUpload} onSave={onSavePhoto} />}
      </Col>
      </Form.Item>
      </Form>
      {item.dolg && !item.clientId ? (
        <div>
          <Button onClick={() => setShowPayment(true)} type="primary" className="sport-btn-mobile-pay">
            Оплатить {item.dolg}
          </Button>
          <Popconfirm title="Подтверждение" description="Удалить клиента?" onConfirm={onDeleteClient}
            placement="top" getPopupContainer={(trigger) => trigger.closest('.client-item-line-double-halfed')} overlayClassName="centered-popconfirm">
            <Button style={{ width: "49.5%" }}>Удалить</Button>
          </Popconfirm>
        </div>
      ) : !item.clientId ? (
        <div>
          <Popconfirm title="Подтверждение" description="Удалить клиента?" onConfirm={onDeleteClient}
            placement="top" getPopupContainer={(trigger) => trigger.closest('.client-item-line-double-halfed')} overlayClassName="centered-popconfirm">
            <Button style={{ width: "100%" }}>Удалить</Button>
          </Popconfirm>
        </div>
      ) : null}
    </>

  return (<>
    {clientCard}
    <div className="payment-window">
      {showPayment && <PaymentAdd clientId={item.id} visitId={null} onClose={onClosePayment} />}
    </div>
    {isMobile ? (
      scanActive && <ScanMobile onClose={() => setScanActive(false)}
        onScan={(card) => {
          form.setFieldsValue({ card });
          setItem(prev => ({ ...prev, card }));
          const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
          setIsFormValid(!hasErrors);
        }} />
    ) : (
      <Scan onScan={handleScan} />
    )}

  </>
  );
};

export default ClientShared;
